/**
 * First-touch acquisition stash for signup attribution.
 *
 * Writes a small, first-party, keep-first acquisition record on page load so
 * the backend can attribute a later signup to the surface, SEO dimension and
 * traffic channel that brought the visitor in. Pure client-side write: no
 * origin Set-Cookie is involved, so this survives CDN cache hits on the
 * directory pages (the cached HTML carries no per-visitor state, the cookie is
 * written by this script in the browser).
 *
 * The stash is read SERVER-SIDE at signup intake (see
 * Interpals\Domain\Service\Seo\SignupAttributionService), which is why this
 * module persists to a cookie (sent on every request) plus a localStorage
 * mirror (resilience against cookie loss across the multi-step funnel).
 *
 * Keep-first: an existing, non-expired stash is never overwritten, so the
 * original entry surface wins even if the visitor later lands on the homepage
 * before signing up.
 *
 * Privacy:
 * - referrer_host is the eTLD+1 of document.referrer only (never the full
 *   referrer URL or its query string).
 * - landing_path is captured only when it matches the public directory path
 *   pattern, and the query string is always dropped.
 * - No identifier of any kind is placed in a URL.
 */

// Cookie name. Mirrors the PHP constant
// Interpals\Domain\Service\Seo\SignupAttributionService::STASH_COOKIE_NAME —
// keep the two in sync.
const STASH_COOKIE_NAME = 'ip_acq';

// First-touch window. A stash older than this is treated as absent and
// rewritten, so a returning visitor is re-attributed rather than credited to a
// stale month-old touch. Matches
// SignupAttributionService::STASH_MAX_AGE_SECONDS.
const STASH_MAX_AGE_DAYS = 30;
const STASH_MAX_AGE_SECONDS = STASH_MAX_AGE_DAYS * 24 * 60 * 60;

// localStorage mirror key (cookie loss resilience across the funnel).
const STASH_STORAGE_KEY = 'ip/acq-stash';

// Directory path pattern. Only public SEO directory landing paths are stored,
// and only the path (no query). Mirrors the server-side guard in
// SignupAttributionService.
const DIRECTORY_PATH_RE = /^\/(language|country)\//i;

/**
 * eTLD+1-ish registrable domain of a referrer host. We deliberately keep this
 * to the last two labels (e.g. "www.google.co.uk" -> "google.co.uk" is not
 * attempted, it yields "co.uk") because a full public-suffix list is overkill
 * here: the server-side classifier matches search engines by a leading-label
 * allow-list ("google.", "bing.com", ...), so a coarse host is enough and
 * keeps PII minimal. Returns '' for same-origin or empty referrers.
 */
function referrerHost(referrer, currentHost) {
    if (!referrer) {
        return '';
    }
    let host;
    try {
        host = new URL(referrer).hostname.toLowerCase();
    } catch (_) {
        return '';
    }
    if (!host || host === currentHost) {
        return '';
    }
    host = host.replace(/^www\./, '');
    const labels = host.split('.');
    if (labels.length <= 2) {
        return host;
    }
    // Keep the last three labels for multi-part TLDs (co.uk, com.br), the last
    // two otherwise. This is a heuristic, not a public-suffix lookup.
    const secondLevel = labels[labels.length - 2];
    const isMultiPartTld = secondLevel.length <= 3;
    return labels.slice(isMultiPartTld ? -3 : -2).join('.');
}

/**
 * Reads the directory dimension the page rendered into the data island, if
 * present. The directory templates emit a
 * <script type="application/json" id="ip-acq-page"> block with page_type and
 * seo_keys so this survives CDN caching (it is part of the cached HTML).
 */
function readPageDimension() {
    const el = document.getElementById('ip-acq-page');
    if (!el) {
        return null;
    }
    try {
        const data = JSON.parse(el.textContent || '{}');
        if (!data || typeof data.page_type !== 'string') {
            return null;
        }
        return {
            page_type: data.page_type,
            seo_keys: Array.isArray(data.seo_keys)
                ? data.seo_keys.filter((k) => typeof k === 'string' && k !== '')
                : [],
        };
    } catch (_) {
        return null;
    }
}

function readUtm(params) {
    const utm = {};
    ['utm_source', 'utm_medium', 'utm_campaign'].forEach((key) => {
        const value = params.get(key);
        if (value) {
            // Cap each value defensively — query strings are attacker-controlled.
            utm[key] = value.slice(0, 128);
        }
    });
    return utm;
}

/**
 * The current stash if one exists and is within the first-touch window.
 * Prefers the cookie (authoritative, server-readable); falls back to the
 * localStorage mirror so a dropped cookie does not reset first-touch.
 */
function existingStash() {
    const fromCookie = readCookie(STASH_COOKIE_NAME);
    const parsed = parseStash(fromCookie) || parseStash(safeStorageGet(STASH_STORAGE_KEY));
    if (!parsed) {
        return null;
    }
    const firstTouchAt = Number(parsed.first_touch_at) || 0;
    if (!firstTouchAt) {
        return null;
    }
    const ageSeconds = Math.floor(Date.now() / 1000) - firstTouchAt;
    if (ageSeconds < 0 || ageSeconds > STASH_MAX_AGE_SECONDS) {
        return null;
    }
    return parsed;
}

function parseStash(raw) {
    if (!raw) {
        return null;
    }
    try {
        const decoded = decodeURIComponent(raw);
        const parsed = JSON.parse(decoded);
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (_) {
        return null;
    }
}

function readCookie(name) {
    const match = document.cookie.match(
        new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'),
    );
    return match ? match[1] : null;
}

/**
 * Registrable cookie domain so the stash is shared across www / apex / api
 * subdomains and survives the OAuth bounce. Mirrors PHP Core\Cookie's leading-
 * dot apex scoping. Falls back to host-only on unusual hostnames.
 */
function cookieDomain(hostname) {
    if (!hostname || /^[0-9.]+$/.test(hostname) || hostname === 'localhost') {
        return '';
    }
    const labels = hostname.replace(/^(www|local)\./, '').split('.');
    if (labels.length < 2) {
        return '';
    }
    const secondLevel = labels[labels.length - 2];
    const isMultiPartTld = secondLevel.length <= 3 && labels.length >= 3;
    return '.' + labels.slice(isMultiPartTld ? -3 : -2).join('.');
}

function writeStash(stash) {
    const value = encodeURIComponent(JSON.stringify(stash));

    const domain = cookieDomain(window.location.hostname);
    const parts = [
        STASH_COOKIE_NAME + '=' + value,
        'path=/',
        'max-age=' + STASH_MAX_AGE_SECONDS,
        'SameSite=Lax',
    ];
    if (domain) {
        parts.push('domain=' + domain);
    }
    if (window.location.protocol === 'https:') {
        parts.push('Secure');
    }
    document.cookie = parts.join('; ');

    safeStorageSet(STASH_STORAGE_KEY, JSON.stringify(stash));
}

function safeStorageGet(key) {
    try {
        return window.localStorage.getItem(key);
    } catch (_) {
        return null;
    }
}

function safeStorageSet(key, value) {
    try {
        window.localStorage.setItem(key, value);
    } catch (_) {
        // Private mode / quota — the cookie is the primary store, ignore.
    }
}

function captureAcquisition() {
    try {
        if (existingStash()) {
            return; // keep-first
        }

        const params = new URLSearchParams(window.location.search);
        const dimension = readPageDimension();
        const path = window.location.pathname;
        const isDirectoryLanding = DIRECTORY_PATH_RE.test(path);

        const stash = {
            surface: isDirectoryLanding ? 'directory' : 'site',
            page_type: dimension ? dimension.page_type : null,
            seo_keys: dimension ? dimension.seo_keys : [],
            // Path only, no query. Stored only for directory landings.
            landing_path: isDirectoryLanding ? path : null,
            referrer_host: referrerHost(document.referrer, window.location.hostname),
            utm: readUtm(params),
            gclid: params.get('gclid') ? params.get('gclid').slice(0, 128) : null,
            first_touch_at: Math.floor(Date.now() / 1000),
        };

        writeStash(stash);
    } catch (err) {
        // Attribution is best-effort and must never affect the page.
        if (window.Sentry && typeof window.Sentry.captureException === 'function') {
            window.Sentry.captureException(err);
        }
    }
}

if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', captureAcquisition);
    } else {
        captureAcquisition();
    }
}

export { referrerHost, cookieDomain, captureAcquisition };
